import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Button } from "@/components/ui";
import { toast } from "sonner";
import { FolderDown, FileText, PlayCircle, Globe, Link2, Check, ExternalLink, ListChecks, Paperclip, Upload, Trash2, Mail, CalendarClock } from "lucide-react";
import { publicOrigin } from "@/lib/publicUrl";

const SUBMISSION_DOCS = [
  { key: "founder-credibility", name: "Founder & Eagle Credibility one-pager", file: "GoMCO-Founder-Eagle-Credibility.pdf", desc: "Read-first: why a new, AI-native firm led by an Eagle Healthcare operator is the safer choice." },
  { key: "rfp-response", name: "Trillium RFP Response", file: "GoMCO-Trillium-RFP-Response.pdf", desc: "The primary deliverable — full response with embedded live-platform screenshots." },
  { key: "delivery-approach-external", name: "Delivery & Operations Approach", file: "GoMCO-Delivery-Approach-External.pdf", desc: "Turnkey delivery, staffing, and phased timeline (sanitized — no internal economics)." },
  { key: "ai-augmented-external", name: "AI-Augmented Delivery", file: "GoMCO-AI-Augmented-Delivery-External.pdf", desc: "How AI accelerates delivery under licensed human oversight." },
  { key: "pricing-grid", name: "Pricing Grid (RFP §3.5)", file: "GoMCO-Trillium-Pricing-Grid.pdf", desc: "Commercial figures only — released at BAFO." },
  { key: "security-attestations", name: "Security & Compliance Attestations", file: "GoMCO-Security-Compliance-Attestations.pdf", desc: "HIPAA / SOC 2 / NIST 800-53 / NC / SCRM posture summary." },
  { key: "security-summary", name: "Security & Compliance Summary (one-pager)", file: "GoMCO-Security-Compliance-Summary.pdf", desc: "Leave-behind overview: BAA hosting (Aptible, portable to AWS/MCO env), live safeguards, and the roadmap to full HIPAA compliance." },
];

const STEPS = [
  "Download the Submittal-Ready Packet (.zip) below and review the 00-SUBMISSION-CHECKLIST cover.",
  "Verify the assumed membership, counties, and any RFP-specific requirements are current.",
  "Attach the packet to the Trillium Clinical Management Platform RFP submission portal / email.",
  "Paste the Live Demo link and Showcase link into your cover email (copy buttons below).",
  "Note that SOC 2 report, certificates of insurance, and Eagle reference contacts are available under NDA at BAFO.",
];

const ATTACHMENT_SLOTS = [
  { key: "soc2", label: "SOC 2 Report / Letter", hint: "Type 1 or Type 2 attestation — removes the #1 evaluator objection." },
  { key: "coi", label: "Certificate of Insurance", hint: "Cyber liability + Professional E&O COI." },
  { key: "references", label: "Client / Eagle Reference Sheet", hint: "Named reference contacts (with consent)." },
];

const OUTREACH_EMAILS = [
  { key: "email-trillium-eagle", name: "Trillium RFP inquiry — Eagle tie-in (recommended)", file: "GoMCO-Email-Trillium-RFP-Inquiry-Eagle.pdf", desc: "From Amanda Holmes. Opens with the Eagle Healthcare / Omeze Nwankwo connection so the outreach lands warm, then asks if the RFP is still open + anything not on the site." },
  { key: "email-trillium", name: "Trillium RFP inquiry — plain version", file: "GoMCO-Email-Trillium-RFP-Status-Inquiry.pdf", desc: "Same status inquiry without the Eagle framing." },
  { key: "email-insurance", name: "Insurance coverage request (pre–SOC 2)", file: "GoMCO-Email-Insurance-Coverage-Request.pdf", desc: "To your broker: coverages, limits, endorsements, and COI needed before SOC 2 is complete." },
];

const TRILLIUM_TIMELINE = [
  ["RFP Distribution", "December 3, 2025"],
  ["Question Period Deadline", "December 15, 2025"],
  ["RFP Response Deadline", "January 30, 2026"],
  ["Target Vendor Selection", "March 30, 2026"],
  ["Target Implementation", "February 1, 2027 (or earlier)"],
];

const TRILLIUM_LINKS = [
  { label: "Current Service Needs (RFP listing)", url: "https://www.trilliumhealthresources.org/current-service-needs" },
  { label: "RFP – Details (PDF)", url: "https://www.trilliumhealthresources.org/sites/default/files/docs/Request-Opportunities/RFP-Clinical-Management-Platform/Trillium-RFP-Care-Management-Platform.pdf" },
  { label: "Questions & Answers (PDF)", url: "https://www.trilliumhealthresources.org/sites/default/files/docs/Request-Opportunities/RFP-Clinical-Management-Platform/Trillium-RFP-Care-Management-Platform-Question-Answers.pdf" },
  { label: "Request NDA (form)", url: "https://app.smartsheet.com/b/form/0930fdc16f6d45f2a78a314599ff89a1" },
  { label: "Application / Submission (form)", url: "https://app.smartsheet.com/b/form/fe793d24ea404b71a405bff6bb84c61a" },
];

function AttachmentsCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/attachments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const byKey = (k) => (items || []).find((i) => i.key === k);

  const onFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File must be under 8MB");
    setBusy(key);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/admin/attachments", { key, filename: file.name, content_b64: reader.result });
        toast.success("Uploaded — now included in the Submittal Packet");
        load();
      } catch { toast.error("Upload failed"); } finally { setBusy(""); }
    };
    reader.readAsDataURL(file);
  };
  const del = async (key) => { try { await api.delete(`/admin/attachments/${key}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); } };

  return (
    <Card className="p-6 mb-6" data-testid="attachments-card">
      <div className="flex items-center gap-2 mb-1"><Paperclip className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Certificates & attachments</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Upload your real SOC 2 letter and insurance certificate (PDF, &lt;8MB). These are added to the <strong>Submittal-Ready Packet</strong> only — never to the public overview packet. Providing them upfront materially raises your win probability.</p>
      <div className="space-y-2.5">
        {ATTACHMENT_SLOTS.map((s) => {
          const cur = byKey(s.key);
          return (
            <div key={s.key} className="flex items-center gap-3 rounded-md border border-border p-3.5" data-testid={`attach-${s.key}`}>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-foreground">{s.label}</p>
                {cur ? <p className="text-xs text-indigo-700 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> {cur.filename} ({Math.round(cur.size / 1024)} KB)</p>
                     : <p className="text-xs text-muted-foreground">{s.hint}</p>}
              </div>
              {cur && <Button size="sm" variant="ghost" onClick={() => del(s.key)} data-testid={`attach-del-${s.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              <label>
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile(s.key)} data-testid={`attach-input-${s.key}`} />
                <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent transition-colors">
                  <Upload className="h-3.5 w-3.5" /> {busy === s.key ? "Uploading…" : cur ? "Replace" : "Upload"}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

async function blobDownload(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = u; a.download = filename; a.click();
  URL.revokeObjectURL(u);
}

export default function SubmissionCenter() {
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const origin = publicOrigin();
  const links = [
    { key: "demo", label: "Live Demo (for Trillium)", url: `${origin}/demo`, icon: PlayCircle },
    { key: "showcase", label: "Concept Showcase", url: `${origin}/showcase`, icon: Globe },
  ];
  const copy = (k, url) => { try { navigator.clipboard?.writeText(url)?.catch(() => {}); } catch { /* noop */ } setCopied(k); toast.success("Link copied"); setTimeout(() => setCopied(""), 1600); };

  const dlSubmittal = async () => {
    setBusy("packet");
    try { await blobDownload("/docs/submittal-packet/zip", "GoMCO-Trillium-RFP-Submittal-Packet.zip"); }
    catch { toast.error("Download failed"); }
    setBusy("");
  };
  const dlDoc = async (d) => {
    setBusy(d.key);
    try { await blobDownload(`/docs/${d.key}/pdf`, d.file); }
    catch { toast.error("Download failed"); }
    setBusy("");
  };

  return (
    <div className="vc-fade-up" data-testid="submission-center">
      <PageHeader title="Trillium Submission Center" subtitle="Everything that goes to Trillium — download the full packet or any single document. All items here are submission-safe (no internal economics)." />

      <Card className="p-6 mb-6 bg-brand-950 text-white border-0" data-testid="submission-packet-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><FolderDown className="h-6 w-6 text-brand-300" /></div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold">Submittal-Ready Packet</h2>
            <p className="text-sm text-brand-100">The complete, submission-ready ZIP: cover checklist + all {SUBMISSION_DOCS.length} documents below, correctly ordered.</p>
          </div>
          <Button variant="subtle" onClick={dlSubmittal} disabled={busy === "packet"} data-testid="submission-download-packet">
            <FolderDown className="h-4 w-4" /> {busy === "packet" ? "Preparing…" : "Download Packet (.zip)"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 mb-6" data-testid="submission-docs">
        <h2 className="font-display text-xl font-bold mb-1">Documents included</h2>
        <p className="text-sm text-muted-foreground mb-4">Download any individual document as a PDF.</p>
        <div className="space-y-2.5">
          {SUBMISSION_DOCS.map((d, i) => (
            <div key={d.key} className="flex items-start gap-3 rounded-md border border-border p-3.5" data-testid={`submission-doc-${d.key}`}>
              <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-foreground">{d.name}</p>
                <p className="text-xs text-muted-foreground">{d.desc}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => dlDoc(d)} disabled={busy === d.key} data-testid={`submission-pdf-${d.key}`}>
                <FileText className="h-3.5 w-3.5" /> {busy === d.key ? "…" : "PDF"}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <AttachmentsCard />

      <Card className="p-6 mb-6" data-testid="submission-emails">
        <div className="flex items-center gap-2 mb-1"><Mail className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Outreach emails (drafts)</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Ready-to-send drafts. Download the PDF, fill the <span className="font-mono text-xs">[bracketed]</span> fields (name, email, phone, dates), then paste into your mail client.</p>
        <div className="space-y-2.5">
          {OUTREACH_EMAILS.map((d) => (
            <div key={d.key} className="flex items-start gap-3 rounded-md border border-border p-3.5" data-testid={`submission-email-${d.key}`}>
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-foreground">{d.name}</p>
                <p className="text-xs text-muted-foreground">{d.desc}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => dlDoc(d)} disabled={busy === d.key} data-testid={`submission-email-pdf-${d.key}`}>
                <FileText className="h-3.5 w-3.5" /> {busy === d.key ? "…" : "PDF"}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 mb-6" data-testid="submission-trillium-rfp">
        <div className="flex items-center gap-2 mb-1"><CalendarClock className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Trillium RFP — official links & timeline</h2></div>
        <p className="text-sm text-muted-foreground mb-4">From Trillium's public "Current Service Needs" page (timelines are tentative). Use the NDA and Application forms to submit — they are not part of the RFP text itself.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Timeline</p>
            <div className="rounded-md border border-border overflow-hidden">
              {TRILLIUM_TIMELINE.map(([k, v], i) => (
                <div key={k} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${i % 2 ? "bg-muted" : ""}`} data-testid={`trillium-timeline-${i}`}>
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium text-foreground text-right">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-amber-700">Note: the Jan 30, 2026 response deadline has passed — confirm the RFP is still open (use the email drafts above).</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Official links</p>
            <div className="space-y-2">
              {TRILLIUM_LINKS.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-brand-700 hover:bg-brand-50 transition-colors" data-testid={`trillium-link-${l.label.split(" ")[0].toLowerCase()}`}>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{l.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="submission-links">
          <h2 className="font-display text-xl font-bold mb-1">Links for your cover email</h2>
          <p className="text-sm text-muted-foreground mb-4">Include these so Trillium can explore the live product without opening files.</p>
          <div className="space-y-3">
            {links.map((l) => (
              <div key={l.key} className="rounded-md border border-border p-3">
                <div className="flex items-center gap-2"><l.icon className="h-4 w-4 text-brand-700" /><p className="font-display font-semibold text-sm">{l.label}</p></div>
                <code className="mt-2 block max-w-full truncate rounded bg-slate-100 px-2 py-1 text-[11px] text-muted-foreground">{l.url}</code>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => copy(l.key, l.url)} data-testid={`submission-copy-${l.key}`}>{copied === l.key ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />} {copied === l.key ? "Copied" : "Copy"}</Button>
                  <a href={l.url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /> Open</Button></a>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="submission-checklist">
          <div className="flex items-center gap-2 mb-1"><ListChecks className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Submission checklist</h2></div>
          <p className="text-sm text-muted-foreground mb-4">Run through this before you hit send.</p>
          <ol className="space-y-2.5">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-brand-300" />
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}
